import { inject, Injectable } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { HttpClient } from '@angular/common/http'
import { ANGULAR_COMMON_CONFIG, AngularCommonEnvironmentModel, PREFERRED_LANGUAGE } from '../models'
import { catchError, firstValueFrom, Observable, of, startWith } from 'rxjs'
import { Language } from '@monorepo/translation-system-generated'
import { map, shareReplay } from 'rxjs/operators'

export interface LanguageSpec {
  code: string
  name: string
}

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private readonly translateService = inject(TranslateService)
  private readonly preferredLanguage$ = inject(PREFERRED_LANGUAGE)
  private readonly http = inject(HttpClient)
  private readonly angularCommonConfig = inject<AngularCommonEnvironmentModel>(ANGULAR_COMMON_CONFIG)

  private readonly languages$: Observable<Language[]> = this.http
    .get<
      Language[]
    >(`${this.angularCommonConfig.translationServiceURL}/languages-translations/app/${this.angularCommonConfig.appName}/languages`)
    .pipe(
      catchError((err) => {
        console.error('Error fetching languages:', err)
        return of<Language[]>([])
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    )

  readonly originalLanguageNames$: Observable<Record<string, string>> = this.languages$.pipe(
    map((languages) =>
      languages.reduce<Record<string, string>>((acc, l) => {
        acc[l.code] = l.nativeName
        return acc
      }, {}),
    ),
    shareReplay({ bufferSize: 1, refCount: false }),
  )

  getOriginalLanguageName$(language: string | null | undefined): Observable<string> {
    return this.originalLanguageNames$.pipe(map((names) => (language ? names[language] : '')))
  }

  readonly sortedLanguages$: Observable<LanguageSpec[]> = this.languages$.pipe(
    map((languages) => {
      const codes = languages.map((l) => l.code)
      return codes
        .map((code) => ({
          code,
          name: this.translateService.instant(`languages.${code}`),
        }))
        .sort((a, b) => a.name.localeCompare(b.name))
    }),
    shareReplay({ bufferSize: 1, refCount: false }),
  )

  readonly sortedLanguagesWithOriginalNames$: Observable<LanguageSpec[]> = this.languages$.pipe(
    map((languages) => {
      const codes = languages.map((l) => l.code)
      return codes
        .map((code) => ({
          code,
          name: this.translateService.instant(`languages.${code}`),
          originalName: languages.find((l) => l.code === code)?.nativeName ?? '',
        }))
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(({ code, name, originalName }) => ({
          code,
          name: `${name} - ${originalName}`,
        }))
    }),
  )

  initializeLanguages(): void {
    this.findLanguageToUse().then((lang) => {
      this.translateService.use(lang)
    })
  }

  private async findLanguageToUse(): Promise<string> {
    const profileLanguage = await firstValueFrom(this.preferredLanguage$)

    if (profileLanguage) {
      return profileLanguage
    }
    const getDesiredLanguage = (): string => {
      const browserLanguage = this.translateService.getBrowserLang()
      if (browserLanguage) {
        return browserLanguage
      }
      return ''
    }

    const isLanguageSupported = async (language: string): Promise<boolean> => {
      const languages = await firstValueFrom(this.sortedLanguages$)
      return languages.some((l) => l.code === language)
    }

    const desiredLanguage = getDesiredLanguage()
    if (desiredLanguage && (await isLanguageSupported(desiredLanguage))) {
      return desiredLanguage
    }

    return 'en'
  }

  selectLanguage(selectedLanguage?: string | null) {
    const lang = selectedLanguage ?? undefined
    if (!lang) return
    if (lang !== this.translateService.currentLang()) {
      this.translateService.use(lang)
    }
  }

  getCurrentLanguage$(): Observable<string> {
    return this.translateService.onLangChange.pipe(
      map((event) => event.lang),
      startWith(this.translateService.getCurrentLang() ?? 'en'),
    )
  }

  getUTLink(): Observable<string> {
    const baseUtLink = 'https://www.utwente.nl/'
    return this.getCurrentLanguage$().pipe(
      map((lang) => {
        switch (lang) {
          case 'nl':
            return baseUtLink
          case 'de':
            return baseUtLink + 'de/'
          case 'it':
            return baseUtLink + 'italy/'
          case 'es':
            return baseUtLink + 'latam/'
          default:
            return baseUtLink + 'en/'
        }
      }),
    )
  }
}
